import { useEffect, type ReactNode } from 'react'
import clsx from 'clsx'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Button } from './Button'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  /** Optionaler Text oder Elemente unter dem Titel (z. B. Kontextzeile). */
  description?: ReactNode
  /** Aktionen rechts neben dem Titelblock, links vom Schließen-Button (z. B. Icon-Buttons). */
  headerActions?: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: ModalSize
  /** Wenn false, kein äußerer Klick zum Schließen (z. B. kritische Dialoge) */
  closeOnBackdrop?: boolean
  /** Zusätzliche Klassen für die Backdrop-Fläche (z. B. z-[60] bei gestapelten Dialogen). */
  backdropClassName?: string
  /** Flex-Ausrichtung des Overlays (Standard: zentriert). */
  positionClassName?: string
  /** Zusätzliche Klassen für den weißen Dialogkörper (z. B. max-h-…). */
  panelClassName?: string
}

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-4xl'
}

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  headerActions,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  backdropClassName,
  positionClassName = 'items-center justify-center',
  panelClassName
}: ModalProps) {
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [isOpen])

  if (!isOpen) return null

  const titleId = title ? 'lr-modal-title' : undefined

  return createPortal(
    <div
      className={clsx(
        'fixed inset-0 z-50 flex p-4 bg-black/50 dark:bg-black/70',
        positionClassName,
        backdropClassName
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onMouseDown={
        closeOnBackdrop
          ? (e) => {
              if (e.target === e.currentTarget) onClose()
            }
          : undefined
      }
    >
      <div
        className={clsx(
          'flex max-h-[min(90vh,900px)] w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40',
          sizeClass[size],
          panelClassName
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="min-w-0 flex-1">
            {title ? (
              <h2 id={titleId} className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
                {title}
              </h2>
            ) : null}
            {description ? (
              <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</div>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {headerActions}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="shrink-0 px-2"
              aria-label="Schließen"
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-400" strokeWidth={2} />
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 text-gray-900 dark:text-gray-100">{children}</div>

        {footer ? (
          <div className="shrink-0 border-t border-gray-200 px-4 py-3 dark:border-gray-700">{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body
  )
}
